import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Grid,
  Column,
  FormGroup,
  Tile,
  Stack,
  ToastNotification,
  TextInput,
  Toggle,
  Button,
} from "@carbon/react";
import CommonFields from "../../common/CommonField";

const Amenities = ({
  propertyUuid = "",
  data = {},
  attributeValues = [],
  formMode = "add",
  currentStep = 4,
  lastSavedStep = 0,
  createProperty,
  saveSectionAttributes,
  onSaveSuccess,
  onComplete,
  onSaved,
  onBack,
  CommonButtons,
  onNavigateNext,
}) => {
  // ------------------------------ CHILD EDIT MODE
  const isEditForm = useMemo(() => {
    return formMode === "edit" || lastSavedStep >= currentStep;
  }, [formMode, lastSavedStep, currentStep]);

  // ------------------------------ STATE
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [touched, setTouched] = useState({});

  // ------------------------------ FIELD MAP WITH RESALE PREFIX
  const FIELD_MAP = useMemo(() => ({
    bathrooms: "resale_bathrooms",
    balcony: "resale_balcony", 
    waterSupply: "resale_water_supply",
    gym: "resale_gym",
    powerBackup: "resale_power_backup",
    gatedSecurity: "resale_gated_security",
    propertyCondition: "resale_property_condition",
  }), []);

  // ------------------------------ FORM STATE
  const [form, setForm] = useState({
    bathrooms: 1,
    balcony: 0,
    waterSupply: "",
    gym: false,
    powerBackup: "",
    gatedSecurity: false,
    propertyCondition: "",
    amenities: [],
  });

  // ------------------------------ DEBUG LOG
  useEffect(() => {
    console.log('🔍 Resale Amenities Debug:', {
      isEditForm,
      currentStep,
      lastSavedStep,
      propertyUuid,
      hasData: !!data,
      amenitiesData: data?.amenities,
      attributesData: data?.attributes
    });
  }, [isEditForm, currentStep, lastSavedStep, propertyUuid, data]);

  const requiredLabel = (text) => (
    <>
      {text} <span style={{ color: "#da1e28" }}>*</span>
    </>
  );

  // ------------------------------ LOAD INITIAL DATA
  useEffect(() => {
    if (!data) {
      console.log('💧 No data provided to Resale Amenities');
      return;
    }

    // Check all possible data sources
    const sourceData = data?.amenities || data?.attributes || {};
    console.log('💧 Loading from source:', sourceData);

    const incoming = {};

    Object.entries(FIELD_MAP).forEach(([field, apiKey]) => {
      const raw = sourceData[apiKey];
      
      if (raw === undefined || raw === null || raw === "") {
        console.log(`  - ${field}: No data for ${apiKey}`);
        return;
      }

      console.log(`  - ${field}: ${raw} (${typeof raw})`);

      // Handle boolean fields
      if (
        field === "gym" || 
        field === "gatedSecurity" || 
        field === "similarPropertiesAvailable"
      ) {
        // Convert to boolean
        incoming[field] = Boolean(
          raw === true || 
          raw === 1 || 
          raw === "1" || 
          raw === "true" || 
          raw === "yes" || 
          raw === "Yes"
        );
      }
      // Handle amenities array
      else if (field === "amenities") {
        if (Array.isArray(raw)) {
          incoming[field] = raw.map(x => String(x));
        } else if (typeof raw === 'string') {
          // Try to parse as JSON or comma-separated
          try {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) {
              incoming[field] = parsed.map(x => String(x));
            } else {
              incoming[field] = [String(raw)];
            }
          } catch {
            incoming[field] = raw.split(',').map(x => String(x.trim())).filter(x => x);
          }
        } else {
          incoming[field] = [String(raw)];
        }
      }
      // Handle numeric fields
      else if (field === "bathrooms" || field === "balcony") {
        incoming[field] = Number(raw) || 0;
      }
      // Handle other fields
      else {
        incoming[field] = String(raw);
      }
    });

    console.log('💧 Incoming form data:', incoming);
    
    if (Object.keys(incoming).length > 0) {
      setForm(prev => {
        const newForm = { ...prev, ...incoming };
        console.log('💧 Updated form:', newForm);
        return newForm;
      });
    }
  }, [data, FIELD_MAP]);

  // ------------------------------ FIELD UPDATES
  const updateField = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setTouched((prev) => ({ ...prev, [key]: true }));
  }, []);

  const handleBlur = useCallback((field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  }, []);

  // ------------------------------ GET OPTIONS
  const getOptions = useCallback(
    (fieldKey) => {
      const apiKey = FIELD_MAP[fieldKey];
      if (!apiKey) return [];

      return attributeValues
        .filter((a) => a.attrName === apiKey)
        .map((a) => ({
          id: a.id,
          label: a.attr_val_name || a.label || a.value,
          attr_val_name: a.attr_val_name,
        }));
    },
    [attributeValues, FIELD_MAP]
  );

  // ------------------------------ VALIDATION - Only bathrooms is required
  const validationRules = useMemo(() => ({
    bathrooms: {
      required: true,
      message: "Bathrooms are required",
      custom: (v) =>
        !v || Number(v) <= 0
          ? "At least one bathroom is required"
          : null,
    },
    // Removed validation for waterSupply, powerBackup, propertyCondition
  }), []);

  const getFieldError = useCallback((field) => {
    const rule = validationRules[field];
    if (!rule) return null;

    const val = form[field];
    if (rule.required && (val === "" || val === null || val === undefined)) {
      return rule.message;
    }
    if (rule.custom) return rule.custom(val);
    return null;
  }, [form, validationRules]);

  // ------------------------------ COMMON FIELDS
  const { renderDropdown } = CommonFields({
    form,
    touched,
    getFieldError,
    handleBlur,
    updateField,
    getOptions,
  });

  // ------------------------------ SAVE HANDLER
  const handleSave = useCallback(async (navigateNext = false) => {
    console.log('💾 Resale Amenities handleSave:', { 
      navigateNext, 
      isEditForm, 
      currentStep,
      propertyUuid,
      formData: form 
    });

    // Mark all fields as touched
    setTouched(Object.keys(form).reduce((acc, f) => ({ ...acc, [f]: true }), {}));

    // Validation - Only check bathrooms
    const errors = [];
    if (!form.bathrooms || Number(form.bathrooms) <= 0) {
      errors.push("Bathrooms are required");
    }

    if (errors.length > 0) {
      setError(errors.join(". "));
      return;
    }

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      let uuid = propertyUuid;

      // Create property if it doesn't exist
      if (!uuid && createProperty) {
        console.log('🆕 Creating property from Resale Amenities Step...');
        uuid = await createProperty();
        onSaveSuccess?.(uuid, form, currentStep);
      }

      if (!uuid) {
        throw new Error("Property UUID is required");
      }

      // Build payload with resale_ prefix
      const payload = {};

      for (const [field, apiKey] of Object.entries(FIELD_MAP)) {
        const value = form[field];
        
        // Skip empty values except required booleans
        if (value === "" || value === null || value === undefined) {
          if (field === "gym" || field === "gatedSecurity") {
            // These are optional now, only send if they have a value
            continue;
          }
          continue;
        }
        
        if (field === "amenities") {
          if (Array.isArray(value) && value.length > 0) {
            payload[apiKey] = value.map(id => Number(id));
          }
        } 
        // Handle boolean fields (convert to 1/0 for API)
        else if (
          field === "gym" || 
          field === "gatedSecurity" || 
          field === "similarPropertiesAvailable"
        ) {
          payload[apiKey] = value ? 1 : 0;
        }
        // Handle numeric fields
        else if (field === "bathrooms" || field === "balcony") {
          payload[apiKey] = Number(value) || 0;
        }
        // Handle dropdown fields
        else if (field === "waterSupply" || field === "propertyCondition" || field === "powerBackup") {
          payload[apiKey] = value ? Number(value) : "";
        }
        // Handle text fields
        else {
          payload[apiKey] = String(value || "");
        }
      }

      console.log('💾 Saving resale amenities payload:', payload);
      
      // Save to API
      await saveSectionAttributes(uuid, payload, form, "amenities");

      setSuccess("Amenities saved successfully!");
      
      // Notify parent
      onSaveSuccess?.(uuid, form, currentStep);
      
      // Call onComplete if provided
      onComplete?.(form);

      // Navigate if requested
      if (navigateNext) {
        console.log('➡️ Navigating to next step after save');
        onNavigateNext?.(form);
      }
    } catch (err) {
      console.error('❌ Save failed:', err);
      setError(err.message || "Failed to save amenities");
    } finally {
      setSaving(false);
    }
  }, [
    form,
    isEditForm,
    currentStep,
    propertyUuid,
    createProperty,
    onSaveSuccess,
    FIELD_MAP,
    saveSectionAttributes,
    onComplete,
    onNavigateNext
  ]);

  // ------------------------------ BUTTON HANDLERS
  const handleSaveOnly = useCallback((e) => {
    e?.preventDefault();
    handleSave(false);
  }, [handleSave]);

  const handleSaveAndNext = useCallback((e) => {
    handleSave(true);
  }, [handleSave]);

  // ------------------------------ UI COMPONENTS
  // Counter component
  const renderCounter = (fieldKey, label, min = 0, required = false) => {
    const value = form[fieldKey] || 0;
    const error = touched[fieldKey] && required && value < min 
      ? `${label} must be at least ${min}` 
      : null;

    return (
      <div>
        <label
          style={{
            fontWeight: "600",
            display: "block",
            marginBottom: "0.5rem",
          }}
        >
          {required ? requiredLabel(label) : label}
        </label>
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
          <Button
            kind="secondary"
            size="sm"
            onClick={() => updateField(fieldKey, Math.max(min, value - 1))}
            disabled={value <= min}
          >
            -
          </Button>
          <TextInput
            id={fieldKey}
            value={value}
            readOnly
            style={{ width: "80px", textAlign: "center" }}
            invalid={!!error}
            invalidText={error}
          />
          <Button
            kind="secondary"
            size="sm"
            onClick={() => updateField(fieldKey, value + 1)}
          >
            +
          </Button>
        </div>
      </div>
    );
  };

  // Toggle component
  const renderToggle = (fieldKey, label, required = false) => {
    const value = form[fieldKey];
    const error = touched[fieldKey] && required && value === undefined 
      ? `${label} is required` 
      : null;

    return (
      <div style={{ marginTop: "1rem" }}>
        <Toggle
          id={`toggle-${fieldKey}`}
          labelText={required ? requiredLabel(label) : label}
          labelA="No"
          labelB="Yes"
          toggled={!!value}
          onToggle={(checked) => updateField(fieldKey, checked)}
        />
        {error && (
          <p style={{ color: "#da1e28", fontSize: "0.75rem", marginTop: "0.25rem" }}>
            {error}
          </p>
        )}
      </div>
    );
  };

  return (
    <div style={{ background: "#fff",}}>
      {error && (
        <ToastNotification
          kind="error"
          title="Error"
          subtitle={error}
          timeout={4000}
          onClose={() => setError("")}
          style={{ position: "fixed", top: "1rem", right: "1rem", zIndex: 9999 }}
        />
      )}

      {success && (
        <ToastNotification
          kind="success"
          title="Success"
          subtitle={success}
          lowContrast
          timeout={3000}
          onClose={() => setSuccess("")}
          style={{ position: "fixed", top: "1rem", right: "1rem", zIndex: 9999 }}
        />
      )}

      <Grid>
        <Column lg={16}>
          <Tile style={{ padding: "2rem", backgroundColor: "white" }}>
            <Stack gap={6}>
              {/* INFRASTRUCTURE */}
              <FormGroup legendText="Infrastructure">
                <Grid>
                  <Column lg={4}>
                    {renderCounter("bathrooms", "Bathroom(s)", 1, true)}
                  </Column>
                  <Column lg={4}>
                    {renderCounter("balcony", "Balcony(s)", 0)}
                  </Column>
                  <Column lg={4}>
                    {renderDropdown("waterSupply", "Water Supply", false)}
                  </Column>
                </Grid>
              </FormGroup>

              {/* FACILITIES */}
              <FormGroup legendText="Facilities">
                <Grid>
                  <Column lg={4}>
                    {renderToggle("gym", "Gym Facility", false)}
                  </Column>
                  <Column lg={4}>
                    {renderDropdown("powerBackup", "Power Backup", false)}
                  </Column>
                  <Column lg={4}>
                    {renderToggle("gatedSecurity", "Gated Security", false)}
                  </Column>
                  <Column lg={4}>
                    {renderDropdown("propertyCondition", "Property Condition", false)}
                  </Column>
                </Grid>
              </FormGroup>
              

              {/* BUTTONS */}
              {CommonButtons && (
                <CommonButtons
                  onSave={handleSaveOnly}
                  onNext={handleSaveAndNext}
                  showSave={true}
                  showNext={true}
                  isSaveLoading={saving}
                  stepData={form}
                />
              )}
            </Stack>
          </Tile>
        </Column>
      </Grid>
    </div>
  );
};

export default Amenities;